import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { LogViewer } from "@/components/LogViewer";
import { CopyAllLogsButton } from "@/components/log-components/CopyAllLogsButton";
import { Pill, type PillVariant } from "@/components/podkit/status/Pill";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { durationBetweenDates, formatAbsoluteDateTime } from "@/format/time";
import { useEnvironmentClassMap } from "@/hooks/use-environment-class-entries";
import { TrackLocations } from "@/hooks/use-segment";
import { useStreamPrebuildLogs } from "@/hooks/use-stream-prebuild-logs";
import { usePrebuild } from "@/queries/prebuild-queries";
import { Timestamp } from "@bufbuild/protobuf";
import { PrebuildMetadata_PrebuildTrigger, PrebuildPhase } from "gitpod-next-api/gitpod/v1/prebuild_pb";
import { type FC, useMemo } from "react";

type PrebuildLogsModalProps = {
    prebuildId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onRerun?: () => void;
    onCancel?: () => void;
};

export const PrebuildLogsModal: FC<PrebuildLogsModalProps> = ({ prebuildId, open, onOpenChange, onRerun, onCancel }) => {
    const { data: prebuild } = usePrebuild(prebuildId);
    const { environmentClasses } = useEnvironmentClassMap();

    // Stream prebuild logs
    const { logGroups, error: logsError, loadingMessage } = useStreamPrebuildLogs({
        prebuild: prebuild || undefined,
        maxLinesPerGroup: 10000,
    });

    const createdAt = prebuild?.metadata?.createdAt ? new Timestamp(prebuild.metadata.createdAt).toDate() : undefined;
    const completedAt = prebuild?.status?.completionTime
        ? new Timestamp(prebuild.status.completionTime).toDate()
        : undefined;

    const clazzEntry = prebuild?.metadata?.environmentClassId
        ? environmentClasses[prebuild.metadata.environmentClassId]
        : undefined;

    const triggerText = useMemo(() => {
        switch (prebuild?.metadata?.triggeredBy) {
            case PrebuildMetadata_PrebuildTrigger.MANUAL:
                return "Manual";
            case PrebuildMetadata_PrebuildTrigger.SCHEDULED:
                return "Scheduled";
            default:
                return "—";
        }
    }, [prebuild?.metadata?.triggeredBy]);

    const statusInfo = useMemo(() => {
        const phase = prebuild?.status?.phase;
        const desiredPhase = prebuild?.spec?.desiredPhase;

        // Optimistic display: if desired phase is different from current phase, show transitioning state
        if (desiredPhase && desiredPhase !== phase) {
            switch (desiredPhase) {
                case PrebuildPhase.CANCELLED:
                    return { label: "Cancelling", pillVariant: "warning" as PillVariant };
                case PrebuildPhase.DELETED:
                    return { label: "Deleting", pillVariant: "danger" as PillVariant };
            }
        }

        switch (phase) {
            case PrebuildPhase.PENDING:
            case PrebuildPhase.STARTING:
                return { label: "Starting", pillVariant: "warning" as PillVariant };
            case PrebuildPhase.RUNNING:
                return { label: "Running", pillVariant: "success" as PillVariant };
            case PrebuildPhase.STOPPING:
                return { label: "Stopping", pillVariant: "warning" as PillVariant };
            case PrebuildPhase.SNAPSHOTTING:
                return { label: "Snapshotting", pillVariant: "warning" as PillVariant };
            case PrebuildPhase.COMPLETED:
                return { label: "Succeeded", pillVariant: "success" as PillVariant };
            case PrebuildPhase.FAILED:
                return { label: "Failed", pillVariant: "danger" as PillVariant };
            case PrebuildPhase.CANCELLING:
                return { label: "Cancelling", pillVariant: "warning" as PillVariant };
            case PrebuildPhase.CANCELLED:
                return { label: "Cancelled", pillVariant: "neutral" as PillVariant };
            case PrebuildPhase.DELETING:
                return { label: "Deleting", pillVariant: "danger" as PillVariant };
            case PrebuildPhase.DELETED:
                return { label: "Deleted", pillVariant: "neutral" as PillVariant };
            default:
                return { label: "—", pillVariant: "neutral" as PillVariant };
        }
    }, [prebuild?.status?.phase, prebuild?.spec?.desiredPhase]);

    const isCancellingState = useMemo(() => {
        const phase = prebuild?.status?.phase;
        const desiredPhase = prebuild?.spec?.desiredPhase;
        
        // We're cancelling if:
        // 1. Phase is CANCELLING, or
        // 2. Desired phase is CANCELLED but current phase is not yet CANCELLED
        return (
            phase === PrebuildPhase.CANCELLING ||
            (desiredPhase === PrebuildPhase.CANCELLED && phase !== PrebuildPhase.CANCELLED)
        );
    }, [prebuild?.status?.phase, prebuild?.spec?.desiredPhase]);

    const isRunning = useMemo(() => {
        const phase = prebuild?.status?.phase;
        
        // Consider it running if phase is actively running (but not cancelling)
        return (
            phase === PrebuildPhase.PENDING ||
            phase === PrebuildPhase.STARTING ||
            phase === PrebuildPhase.RUNNING ||
            phase === PrebuildPhase.STOPPING ||
            phase === PrebuildPhase.SNAPSHOTTING
        );
    }, [prebuild?.status?.phase]);

    const isStopped = useMemo(() => {
        const phase = prebuild?.status?.phase;
        return (
            phase === PrebuildPhase.COMPLETED ||
            phase === PrebuildPhase.FAILED ||
            phase === PrebuildPhase.CANCELLED ||
            phase === PrebuildPhase.DELETED
        );
    }, [prebuild?.status?.phase]);

    const duration = useMemo(() => {
        if (createdAt && completedAt) {
            return durationBetweenDates(createdAt, completedAt);
        }
        // Show live duration for in-progress prebuilds
        if (createdAt && isRunning) {
            return durationBetweenDates(createdAt, new Date());
        }
        return "";
    }, [createdAt, completedAt, isRunning]);

    const errorMessage = prebuild?.status?.failureMessage;
    const warningMessage = prebuild?.status?.warningMessage;

    if (!prebuild) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-track-location={TrackLocations.PrebuildLogsModal}>
                <DialogHeader>
                    <DialogTitle className="text-lg font-medium leading-none">Prebuild logs</DialogTitle>
                    <div className="flex flex-col gap-2 pt-2">
                        <div className="flex items-center gap-2">
                            <span className="text-base font-normal leading-normal text-content-secondary">
                                {clazzEntry
                                    ? `${clazzEntry.clazz.displayName} (${clazzEntry.runner.name})`
                                    : "Unknown class"}
                            </span>
                            <Pill variant={statusInfo.pillVariant} size="sm" className="font-mono text-xs leading-tight">
                                {statusInfo.label}
                            </Pill>
                        </div>
                        <div className="text-sm font-normal leading-normal text-content-tertiary">
                            <span>{triggerText}</span>
                            <span> • </span>
                            <span>{createdAt ? formatAbsoluteDateTime(createdAt) : "—"}</span>
                            {duration && <span> • Duration: {duration}</span>}
                        </div>
                    </div>
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    {errorMessage && <Banner variant="danger" text={errorMessage} className="text-sm" />}

                    {warningMessage && <Banner variant="warning" text={warningMessage} className="text-sm" />}

                    <div className="h-[500px] overflow-hidden rounded-lg">
                        <LogViewer
                            logGroups={logGroups}
                            error={logsError}
                            loadingMessage={loadingMessage}
                            filterLogLinesWithoutTimestamps={true}
                            overscanCount={20}
                        />
                    </div>
                </DialogBody>

                <DialogFooter className="flex items-center justify-between">
                    <CopyAllLogsButton logGroups={logGroups} trackingId="copy-all-logs-prebuild" />

                    <div className="flex gap-2">
                        {(isRunning || isCancellingState) && onCancel && (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={onCancel}
                                disabled={isCancellingState}
                                LeadingIcon={
                                    isCancellingState
                                        ? (props) => <IconSpinner {...props} className="animate-spin" />
                                        : undefined
                                }
                                data-tracking-id="cancel-prebuild"
                            >
                                {isCancellingState ? "Cancelling..." : "Cancel prebuild"}
                            </Button>
                        )}
                        {onRerun && (isStopped || isCancellingState) && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={onRerun}
                                data-tracking-id="rerun-prebuild"
                            >
                                Rerun
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
